import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  DollarSign, Phone, PhoneCall, CreditCard, TrendingUp, 
  Clock, ArrowRight, Activity, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token, updateBalance } = useAuth();
  const [stats, setStats] = useState({
    balance: 0,
    sipAccounts: 0,
    totalCalls: 0,
    totalMinutes: 0
  });
  const [recentCDRs, setRecentCDRs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch balance
        const balanceRes = await axios.get(`${API}/user/balance`, { headers });
        if (balanceRes.data.success) {
          updateBalance(balanceRes.data.credit);
          setStats(prev => ({ ...prev, balance: balanceRes.data.credit }));
        }
        
        // Fetch SIP accounts
        const sipRes = await axios.get(`${API}/user/sip`, { headers });
        if (sipRes.data.success) {
          setStats(prev => ({ ...prev, sipAccounts: sipRes.data.total || sipRes.data.accounts?.length || 0 }));
        }
        
        // Fetch recent CDRs
        const cdrRes = await axios.get(`${API}/user/cdr?limit=5`, { headers });
        if (cdrRes.data.success) {
          setRecentCDRs(cdrRes.data.cdrs || []);
          setStats(prev => ({ 
            ...prev, 
            totalCalls: cdrRes.data.total || 0 
          }));
        }
        
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const statCards = [
    { 
      icon: DollarSign, 
      label: "Balance", 
      value: `$${parseFloat(stats.balance || user?.credit || 0).toFixed(2)}`,
      color: "bg-green-500",
      link: "/portal/recharge"
    },
    { 
      icon: Phone, 
      label: "SIP Accounts", 
      value: stats.sipAccounts,
      color: "bg-blue-500",
      link: "/portal/sip"
    },
    { 
      icon: Activity, 
      label: "Total Calls", 
      value: stats.totalCalls,
      color: "bg-purple-500",
      link: "/portal/cdr"
    },
    { 
      icon: PhoneCall, 
      label: "Softphone", 
      value: "Ready",
      color: "bg-brand-400",
      link: "/portal/softphone"
    }
  ];

  return (
    <div data-testid="dashboard-page">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          Welcome back, {user?.username}!
        </h1>
        <p className="text-slate-500 mt-1">Here's your account overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Link to={stat.link}>
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl p-6 text-white"
        >
          <h3 className="text-xl font-bold mb-2">Start Making Calls</h3>
          <p className="text-brand-50 mb-4">
            Use our WebRTC softphone to make calls directly from your browser.
          </p>
          <Link to="/portal/softphone">
            <Button className="bg-white text-brand-500 hover:bg-brand-25 rounded-full">
              <PhoneCall className="w-4 h-4 mr-2" />
              Open Softphone
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
        >
          <h3 className="text-xl font-bold text-slate-900 mb-2">Low Balance?</h3>
          <p className="text-slate-500 mb-4">
            Recharge with cryptocurrency using OxaPay. Fast and secure.
          </p>
          <Link to="/portal/recharge">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
              <CreditCard className="w-4 h-4 mr-2" />
              Recharge Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Recent Calls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-2xl shadow-sm border border-slate-100"
      >
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Recent Calls</h3>
          <Link to="/portal/cdr" className="text-brand-500 hover:text-brand-600 text-sm font-medium">
            View All <ArrowRight className="w-4 h-4 inline ml-1" />
          </Link>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : recentCDRs.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No calls yet</p>
              <Link to="/portal/softphone" className="text-brand-500 hover:text-brand-600 text-sm font-medium mt-2 inline-block">
                Make your first call
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCDRs.slice(0, 5).map((cdr, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <Globe className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{cdr.calledstation || cdr.dst || "Unknown"}</p>
                      <p className="text-sm text-slate-500">{cdr.starttime || cdr.calldate || "N/A"}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">
                      {cdr.sessiontime || cdr.billsec || 0}s
                    </p>
                    <p className="text-sm text-brand-500">
                      ${parseFloat(cdr.sessionbill || cdr.cost || 0).toFixed(4)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
